import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getClients, promoteToAdmin, demoteToClient, createClient } from '../../services/users';
import { formatDate } from '../../lib/format';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { useToast } from '../../hooks/useToast';
import { getErrorMessage } from '../../lib/errorUtils';
import type { UserResponse } from '../../types/user';

export function ClientDirectory() {
  const { showToast } = useToast();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newClient, setNewClient] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });
  const qc = useQueryClient();

  useEffect(() => {
    const handleOpenModal = () => setShowAddModal(true);
    window.addEventListener('openAddClientModal', handleOpenModal);
    return () => window.removeEventListener('openAddClientModal', handleOpenModal);
  }, []);

  const { data: clientsData, isLoading } = useQuery({
    queryKey: ['clients', search, page],
    queryFn: () => getClients(search, page, 20),
  });

  const promoteMutation = useMutation({
    mutationFn: promoteToAdmin,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
      showToast('User promoted to admin successfully', 'success');
    },
    onError: (err: unknown) => {
      showToast(getErrorMessage(err, 'Failed to promote user'), 'error');
    },
  });

  const demoteMutation = useMutation({
    mutationFn: demoteToClient,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
      showToast('User demoted to client successfully', 'success');
    },
    onError: (err: unknown) => {
      showToast(getErrorMessage(err, 'Failed to demote user'), 'error');
    },
  });

  const createClientMutation = useMutation({
    mutationFn: createClient,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
      setShowAddModal(false);
      setNewClient({ firstName: '', lastName: '', email: '', password: '' });
      setError(null);
      showToast('Client created successfully', 'success');
    },
    onError: (err: unknown) => {
      const message = getErrorMessage(err, 'Failed to create client');
      setError(message);
    },
  });

  const handleAddClient = () => {
    setError(null);
    
    if (!newClient.email || !newClient.firstName || !newClient.lastName || !newClient.password) {
      setError('All fields are required');
      return;
    }
    
    if (newClient.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    createClientMutation.mutate(newClient);
  };

  return (
    <>
      <header className="mb-6 space-y-2 sm:mb-8 sm:space-y-3 lg:mb-12">
        <h1 className="text-2xl font-bold text-primary sm:text-3xl lg:text-4xl">
          Client Directory
        </h1>
        <p className="text-sm leading-relaxed text-on-surface/70 sm:text-base">
          Manage your client relationships and track engagement progress.
        </p>
      </header>

      <section className="mb-6 grid grid-cols-1 gap-4 sm:mb-8 sm:gap-6 md:grid-cols-3">
        <div className="rounded-xl bg-surface-container-low p-6 sm:p-8 shadow-editorial">
          <p className="label-sm mb-2 text-on-surface/60">Total Users</p>
          <h3 className="text-3xl sm:text-4xl font-bold text-primary">{clientsData?.totalElements || 0}</h3>
        </div>
        <div className="rounded-xl bg-surface-container-low p-6 sm:p-8 shadow-editorial">
          <p className="label-sm mb-2 text-on-surface/60">Page</p>
          <h3 className="text-3xl sm:text-4xl font-bold text-primary">{page + 1}</h3>
        </div>
        <div className="rounded-xl bg-surface-container-low p-6 sm:p-8 shadow-editorial">
          <p className="label-sm mb-2 text-on-surface/60">Total Pages</p>
          <h3 className="text-3xl sm:text-4xl font-bold text-primary">{clientsData?.totalPages || 1}</h3>
        </div>
      </section>

      <section className="mb-6 rounded-xl bg-surface-container-low p-4 sm:p-6 lg:p-8 shadow-editorial">
        <div className="mb-4 sm:mb-6 flex flex-col gap-4 sm:gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-md flex-1">
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-on-surface/40 text-xl">
                search
              </span>
              <input
                type="text"
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-xl border-0 bg-surface-container-high py-2.5 sm:py-3 pl-10 sm:pl-12 pr-3 sm:pr-4 text-sm sm:text-base text-on-surface placeholder-on-surface/40 outline-none transition-all focus:bg-surface focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>
          <button 
            onClick={() => setShowAddModal(true)}
            className="flex items-center justify-center gap-2 rounded-md bg-secondary px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-semibold text-white transition-opacity hover:opacity-90 w-full lg:w-auto"
          >
            <span className="material-symbols-outlined text-sm">add</span>
            <span className="hidden sm:inline">Add New Client</span>
            <span className="sm:hidden">Add Client</span>
          </button>
        </div>
      </section>

      <section className="overflow-hidden rounded-xl bg-surface-container-low shadow-editorial mb-8">
        <div className="bg-surface-container p-4 sm:p-6 lg:p-8">
          <h2 className="text-xl sm:text-2xl font-bold text-primary">User List</h2>
          <p className="mt-1 text-xs sm:text-sm text-on-surface/60">
            {clientsData?.totalElements || 0} users found (clients and admins)
          </p>
        </div>

        {isLoading ? (
          <div className="p-12 text-center">
            <div className="inline-flex items-center gap-3 text-on-surface/60">
              <span className="material-symbols-outlined animate-spin">refresh</span>
              <span className="font-semibold">Loading clients...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden overflow-x-auto lg:block">
              <table className="w-full">
                <thead className="bg-surface-container-high">
                  <tr>
                    <th className="p-6 text-left label-sm text-on-surface/70">Client</th>
                    <th className="p-6 text-left label-sm text-on-surface/70">Status</th>
                    <th className="p-6 text-left label-sm text-on-surface/70">Joined</th>
                    <th className="p-6 text-left label-sm text-on-surface/70">Role</th>
                    <th className="p-6 text-right label-sm text-on-surface/70">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-surface-container-lowest">
                  {clientsData?.content.map((client: UserResponse) => (
                    <tr key={client.id} className="transition-colors hover:bg-surface-container-low">
                      <td className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-container">
                            <span className="material-symbols-outlined text-on-primary-container">person</span>
                          </div>
                          <div>
                            <p className="font-bold text-on-surface">
                              {client.firstName} {client.lastName}
                            </p>
                            <p className="text-sm text-on-surface/60">{client.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-6">
                        <span className="chip chip-tertiary">Active</span>
                      </td>
                      <td className="p-6">
                        <span className="text-sm text-on-surface/70">
                          {formatDate(client.createdAt)}
                        </span>
                      </td>
                      <td className="p-6">
                        <span className={`chip ${client.role === 'ADMIN' ? 'chip-primary' : 'chip-tertiary'}`}>
                          {client.role}
                        </span>
                      </td>
                      <td className="p-6 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {client.role === 'CLIENT' ? (
                            <button
                              onClick={() => promoteMutation.mutate(client.id)}
                              disabled={promoteMutation.isPending}
                              className="rounded-md px-4 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary/10"
                            >
                              Promote
                            </button>
                          ) : (
                            <button
                              onClick={() => demoteMutation.mutate(client.id)}
                              disabled={demoteMutation.isPending}
                              className="rounded-md px-4 py-2 text-sm font-semibold text-secondary transition-colors hover:bg-secondary/10"
                            >
                              Demote
                            </button>
                          )}
                          <Link
                            to={`/admin/clients/${client.id}`}
                            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                          >
                            View
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="space-y-4 bg-surface-container-lowest p-4 lg:hidden">
              {clientsData?.content.map((client: UserResponse) => (
                <div key={client.id} className="rounded-xl bg-surface-container-low p-4 shadow-sm">
                  <div className="mb-4 flex items-start gap-3">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary-container">
                      <span className="material-symbols-outlined text-on-primary-container">person</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-on-surface">
                        {client.firstName} {client.lastName}
                      </p>
                      <p className="text-sm text-on-surface/60">{client.email}</p>
                    </div>
                  </div>
                  <div className="mb-4 flex flex-wrap gap-2">
                    <span className="chip chip-tertiary">Active</span>
                    <span className={`chip ${client.role === 'ADMIN' ? 'chip-primary' : 'chip-tertiary'}`}>
                      {client.role}
                    </span>
                    <span className="text-xs text-on-surface/60">
                      Joined {formatDate(client.createdAt)}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    {client.role === 'CLIENT' ? (
                      <button
                        onClick={() => promoteMutation.mutate(client.id)}
                        disabled={promoteMutation.isPending}
                        className="flex-1 rounded-md bg-surface-container-high px-4 py-2 text-sm font-semibold text-primary transition-colors hover:bg-surface-container-highest"
                      >
                        Promote to Admin
                      </button>
                    ) : (
                      <button
                        onClick={() => demoteMutation.mutate(client.id)}
                        disabled={demoteMutation.isPending}
                        className="flex-1 rounded-md bg-surface-container-high px-4 py-2 text-sm font-semibold text-secondary transition-colors hover:bg-surface-container-highest"
                      >
                        Demote to Client
                      </button>
                    )}
                    <Link
                      to={`/admin/clients/${client.id}`}
                      className="flex-1 rounded-md bg-primary px-4 py-2 text-center text-sm font-semibold text-white transition-opacity hover:opacity-90"
                    >
                      View Profile
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {clientsData && clientsData.totalPages > 1 && (
          <div className="flex flex-col items-center justify-between gap-4 bg-surface-container p-4 sm:flex-row sm:p-6">
            <p className="text-xs text-on-surface/60 sm:text-sm">
              <span className="hidden sm:inline">Showing {page * 20 + 1} to {Math.min((page + 1) * 20, clientsData.totalElements)} of </span>
              {clientsData.totalElements} clients
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="rounded-lg p-2 transition-colors hover:bg-surface-container-high disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="material-symbols-outlined">chevron_left</span>
              </button>
              <span className="px-3 py-2 text-xs font-semibold sm:px-4 sm:text-sm">
                Page {page + 1} of {clientsData.totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(clientsData.totalPages - 1, page + 1))}
                disabled={page >= clientsData.totalPages - 1}
                className="rounded-lg p-2 transition-colors hover:bg-surface-container-high disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="material-symbols-outlined">chevron_right</span>
              </button>
            </div>
          </div>
        )}
      </section>

      <Modal
        open={showAddModal}
        title="Add New Client"
        description="Create a new client account with email and password authentication."
        onClose={() => {
          setShowAddModal(false);
          setError(null);
          setNewClient({ firstName: '', lastName: '', email: '', password: '' });
        }}
        footer={
          <>
            <Button 
              variant="ghost" 
              onClick={() => {
                setShowAddModal(false);
                setError(null);
                setNewClient({ firstName: '', lastName: '', email: '', password: '' });
              }}
              disabled={createClientMutation.isPending}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddClient}
              loading={createClientMutation.isPending}
            >
              Create Client
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="First Name"
            value={newClient.firstName}
            onChange={(e) => setNewClient({ ...newClient, firstName: e.target.value })}
            placeholder="John"
            required
          />
          <Input
            label="Last Name"
            value={newClient.lastName}
            onChange={(e) => setNewClient({ ...newClient, lastName: e.target.value })}
            placeholder="Doe"
            required
          />
          <Input
            label="Email"
            type="email"
            value={newClient.email}
            onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
            placeholder="john.doe@example.com"
            required
          />
          <Input
            label="Password"
            type="password"
            value={newClient.password}
            onChange={(e) => setNewClient({ ...newClient, password: e.target.value })}
            placeholder="Minimum 8 characters"
            required
          />
          {error && (
            <p className="text-sm font-semibold text-error">{error}</p>
          )}
        </div>
      </Modal>
    </>
  );
}
